import { apiFetch } from './http';
import { buildEnrollmentProductApiPath } from './routes';
import { SubmissionError } from './submit';

export interface PublicSubmitResponse {
  ok: true;
  result_id: string;
  correlation_id?: string;
  public_token?: string;
}

export interface PublicEligibilityResponse {
  eligible: boolean;
  correlation_id?: string;
  result_id?: string;
}

export interface PublicResultResponse {
  outcome: string;
  createdAt: string;
  cardId?: string;
  bin?: string;
  pcn?: string;
  group?: string;
  title?: string;
  message?: string;
}

interface PublicErrorBody {
  error?: string;
  fieldErrors?: Record<string, string>;
  messages?: string[];
  correlation_id?: string;
  result_id?: string;
}

async function throwPublicError(res: Response, fallbackMessage: string): Promise<never> {
  const body = await res.json().catch(() => ({})) as PublicErrorBody;
  throw new SubmissionError(body.error ?? fallbackMessage, res.status, {
    fieldErrors: body.fieldErrors,
    messages: body.messages,
    correlationId: body.correlation_id,
    resultId: body.result_id,
  });
}

export async function publicValidateEligibility(product: string, values: Record<string, unknown>): Promise<PublicEligibilityResponse> {
  const res = await apiFetch(buildEnrollmentProductApiPath(product, '/public-validate-eligibility'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ values }),
  });

  if (!res.ok) {
    await throwPublicError(res, 'Eligibility check failed');
  }

  return res.json() as Promise<PublicEligibilityResponse>;
}

export async function publicSubmitForm(product: string, values: Record<string, unknown>): Promise<PublicSubmitResponse> {
  const res = await apiFetch(buildEnrollmentProductApiPath(product, '/public-submit'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ values }),
  });

  if (!res.ok) {
    await throwPublicError(res, `Failed to submit enrollment: ${res.status}`);
  }

  return res.json() as Promise<PublicSubmitResponse>;
}

// The token goes in the POST body, never in the request URL.
export async function fetchPublicResult(product: string, publicToken: string): Promise<PublicResultResponse> {
  const res = await apiFetch(buildEnrollmentProductApiPath(product, '/public-results'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ token: publicToken }),
  });

  if (!res.ok) {
    await throwPublicError(res, `Failed to load result: ${res.status}`);
  }

  return res.json() as Promise<PublicResultResponse>;
}
